import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useNcrStore from '../store/ncrStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { exportNcrPdf } from '../services/exportService';
import { downloadBlob } from '../utils/downloadFile';
import { formatDateDDMMYY } from '../utils/formatDate';

const NCRDetailPage = () => {
  const { id } = useParams();
  const { selectedNcr, isLoading, isSubmitting, fetchNCR, closeNCR } = useNcrStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);

  const [verificationNotes, setVerificationNotes] = useState('');

  useEffect(() => {
    fetchNCR(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = async (e) => {
    e.preventDefault();
    const result = await closeNCR(id, { closed_by_id: user?.id, verification_notes: verificationNotes });
    if (result.success) {
      showToast('NCR closed', 'success');
      setVerificationNotes('');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleExportPdf = async () => {
    try {
      const res = await exportNcrPdf(id);
      downloadBlob(res.data, `${selectedNcr.ncr_number}.pdf`);
    } catch (err) {
      showToast('Failed to export PDF', 'error');
    }
  };

  if (isLoading || !selectedNcr) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  const n = selectedNcr;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/ncr" className="text-sm text-white hover:underline">&larr; Back to NCRs</Link>

        <div className="glass-card p-6 mt-3 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 font-mono">{n.ncr_number}</h1>
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 capitalize">{n.status.replace('_', ' ')}</span>
              <button onClick={handleExportPdf} className="text-komal-blue hover:underline text-xs font-semibold">
                Export PDF
              </button>
            </div>
          </div>

          <div className="text-sm space-y-3">
            <div>
              <div className="text-gray-500">Description</div>
              <div className="font-medium">{n.description}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-gray-500">Severity</div>
                <div className="font-medium capitalize">{n.severity}</div>
              </div>
              <div>
                <div className="text-gray-500">Raised Date</div>
                <div className="font-medium">{formatDateDDMMYY(n.raised_date)}</div>
              </div>
            </div>
            {n.root_cause && (
              <div>
                <div className="text-gray-500">Root Cause</div>
                <div>{n.root_cause}</div>
              </div>
            )}
            {n.corrective_action && (
              <div>
                <div className="text-gray-500">Corrective Action</div>
                <div>{n.corrective_action}</div>
              </div>
            )}
            {n.status === 'closed' && (
              <div>
                <div className="text-gray-500">Verification Notes</div>
                <div>{n.verification_notes}</div>
              </div>
            )}
          </div>
        </div>

        {n.status !== 'closed' && (
          <form onSubmit={handleClose} className="glass-card p-6 space-y-3">
            <h2 className="font-semibold text-gray-900">Close NCR</h2>
            <textarea
              rows={3}
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              placeholder="Verification notes..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Closing...' : 'Close NCR'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default NCRDetailPage;
